import * as React from 'react';
import { z } from 'zod';
import { Alert } from './Alert';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import { type DriverTruckOption } from './DriverTruckStatusFields';
import { Field } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import {
  PHONE_WITH_COUNTRY_CODE_MESSAGE,
  PhoneInput,
  isValidPhoneWithCountryCode,
} from './PhoneInput';
import { Select, type SelectOption } from './Select';
import { Loading } from './Spinner';

/** Midnight today, so a start date of today is not "in the future". */
function startOfToday() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

/** The office role value for someone who only drives. */
export const NO_OFFICE_ROLE = 'NONE';

/**
 * Add a person to the company: who they are, the office role they join
 * with, and, if they drive, their driver details. The Team page and the
 * Drivers page both add people with it; each had a form of its own, and
 * the Team page's could not make anyone a driver with a truck or a start
 * date.
 */
export const newUserSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name cannot exceed 100 characters')
      .regex(
        /^[a-zA-Z\s'-]+$/,
        'Name can only contain letters, spaces, hyphens, and apostrophes'
      ),
    email: z
      .string()
      .min(1, 'Email is required')
      .email('Please enter a valid email address')
      .max(100, 'Email cannot exceed 100 characters'),
    phone: z
      .string()
      .optional()
      // A field backspaced to its country code holds no number.
      .transform((v) => (v?.trim() === '+1' ? '' : v))
      .refine(
        (v) => !v || isValidPhoneWithCountryCode(v),
        PHONE_WITH_COUNTRY_CODE_MESSAGE
      ),
    /** The office role they join with; NONE for someone who only drives. */
    officeRole: z
      .enum(['OWNER', 'ADMIN', 'DISPATCH', 'READ_ONLY', NO_OFFICE_ROLE])
      .default('DISPATCH'),
    /** They drive: the fields below are their driver details. */
    drives: z.boolean().default(false),
    /** Empty when no truck is chosen. */
    truckId: z.string().optional(),
    status: z.enum(['ACTIVE', 'ON_LEAVE']).default('ACTIVE'),
    /** As a date input holds it, YYYY-MM-DD; asked for only of a driver. */
    startDate: z.string().optional(),
  })
  .superRefine((values, ctx) => {
    // No office role leaves them only driving, so they must drive.
    if (values.officeRole === NO_OFFICE_ROLE && !values.drives) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['officeRole'],
        message: 'Pick an office role, or add them as a driver.',
      });
    }
    if (!values.drives) return;
    const start = values.startDate;
    if (!start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Start date is required',
      });
      return;
    }
    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    if (new Date(start) > startOfToday()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Start date cannot be in the future',
      });
    } else if (new Date(start) < tenYearsAgo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Start date cannot be more than 10 years ago',
      });
    }
  });

export type NewUserValues = z.infer<typeof newUserSchema>;
export type NewUserForm = ZodForm<typeof newUserSchema>;

export interface NewUserDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Add Team Member" on the Team page, "Add Driver" on the Drivers page. */
  title?: string;
  /** From `useZodForm(newUserSchema, …)`. */
  form: NewUserForm;
  /** Named in the note about the invitation; "your company" when unknown. */
  companyName?: string | null;
  /**
   * The office roles the inviter may give: Owner only for an owner, and
   * No role (value NONE) only for someone who may add drivers.
   */
  roleOptions: SelectOption[];
  /**
   * Offer "Add as driver". Pass whether the inviter may add drivers
   * (`drivers.add`); the server refuses it otherwise.
   */
  canAddDriver?: boolean;
  trucks?: DriverTruckOption[];
  trucksLoading?: boolean;
  /** The plan's driver limits have not come back yet. */
  checkingPlan?: boolean;
  /**
   * Why the plan will not take another driver, shown in place of the
   * driver details while they are asked for; Send is off with it.
   */
  driverGate?: React.ReactNode;
  /** The invitation is being sent: both buttons lock and submit relabels. */
  sending?: boolean;
  onSubmit: (values: NewUserValues) => void;
}

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ON_LEAVE', label: 'On Leave' },
];

/**
 * Add a person: name, email, phone, the office role they join with and,
 * for an inviter who may add drivers, whether they drive. Driving shows the
 * driver details: an employee number the server generates, a truck, a
 * status and a start date. No role means they only drive, so it ticks Add
 * as driver and holds it. A note names the company and what is sent.
 *
 *   const form = useZodForm(newUserSchema, { defaultValues: EMPTY });
 *   <NewUserDialogView open={open} onOpenChange={setOpen} form={form}
 *     title="Add Team Member" companyName="Bayside Towing"
 *     roleOptions={roles} canAddDriver={can('drivers.add')} trucks={trucks}
 *     sending={isPending} onSubmit={invite} />
 *
 * The name field takes focus when the dialog opens. The footer sits
 * outside the form so DialogContent can hoist it clear of the scrolling
 * body; the submit button reaches the form by id.
 */
export function NewUserDialogView({
  open,
  onOpenChange,
  title = 'Add Team Member',
  form,
  companyName,
  roleOptions,
  canAddDriver = false,
  trucks = [],
  trucksLoading = false,
  checkingPlan = false,
  driverGate,
  sending = false,
  onSubmit,
}: NewUserDialogViewProps) {
  const drives = form.watch('drives');
  const driverOnly = form.watch('officeRole') === NO_OFFICE_ROLE;

  React.useEffect(() => {
    if (!open) return;
    // After the dialog has finished mounting.
    const timer = window.setTimeout(() => form.setFocus('name'), 100);
    return () => window.clearTimeout(timer);
  }, [open, form]);

  const truckPlaceholder = trucksLoading
    ? 'Loading trucks...'
    : trucks.length === 0
      ? 'No trucks available'
      : 'Select a truck';
  const gated = drives && !!driverGate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" data-testid="new-user-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Form
          form={form}
          onSubmit={onSubmit}
          id="new-user-form"
          data-testid="new-user-form"
        >
          <FormField form={form} name="name" label="Name">
            {(field) => (
              <Input
                {...field}
                data-testid="invite-name-input"
                placeholder="Enter full name"
              />
            )}
          </FormField>

          <FormField
            form={form}
            name="email"
            label="Email"
            description="An invitation email will be sent to this address."
          >
            {(field) => (
              <Input
                type="email"
                {...field}
                data-testid="invite-email-input"
                placeholder="Enter email address"
              />
            )}
          </FormField>

          <FormField form={form} name="phone" label="Phone Number">
            {(field) => (
              <PhoneInput
                {...field}
                withCountryCode
                data-testid="invite-phone-input"
              />
            )}
          </FormField>

          <FormField form={form} name="officeRole" label="Office role">
            {(field) => (
              <Select
                options={roleOptions}
                value={field.value}
                onValueChange={field.onChange}
                data-testid="invite-role-select"
              />
            )}
          </FormField>

          {canAddDriver && (
            <FormField form={form} name="drives">
              {(field) => (
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) =>
                    field.onChange(checked === true)
                  }
                  disabled={driverOnly}
                  label="Add as driver"
                  description={
                    driverOnly
                      ? 'With no office role, they only drive.'
                      : 'They can be dispatched and use the driver app once they join.'
                  }
                  data-testid="invite-drives-checkbox"
                />
              )}
            </FormField>
          )}

          {canAddDriver && drives && checkingPlan && (
            <Loading label="Checking your plan" pad="sm" />
          )}

          {canAddDriver && drives && !checkingPlan && gated && driverGate}

          {canAddDriver && drives && !checkingPlan && !gated && (
            <>
              <Field label="Employee Number">
                <Input disabled placeholder="Auto generated" />
              </Field>

              <FormField form={form} name="truckId" label="Truck">
                {(field) => (
                  <Select
                    options={trucks.map((truck) => ({
                      value: truck.id,
                      label: truck.label,
                    }))}
                    placeholder={truckPlaceholder}
                    disabled={trucksLoading || trucks.length === 0}
                    value={field.value || undefined}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>

              <FormField form={form} name="status" label="Status">
                {(field) => (
                  <Select
                    options={STATUS_OPTIONS}
                    placeholder="Select status"
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                )}
              </FormField>

              <FormField form={form} name="startDate" label="Start Date">
                {(field) => <Input type="date" {...field} />}
              </FormField>
            </>
          )}

          <Alert tone="info" title="Note">
            {driverOnly
              ? 'An invitation to the driver app will be sent to their email.'
              : `An invitation email will be sent to join ${companyName || 'your company'}.`}
          </Alert>
        </Form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="cancel-invitation-button"
          >
            Cancel
          </Button>
          <Button
            form="new-user-form"
            type="submit"
            disabled={sending || gated || (drives && checkingPlan)}
            data-testid="send-invitation-button"
          >
            {sending ? 'Sending Invitation...' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
